export type IdeaStatus = "raw" | "refining" | "ready" | "filed";

export interface Idea {
  id: string;
  userId: string;
  title: string;
  brainDump: string;
  status: IdeaStatus;
  projectId: string | null;
  preferredRepo: string | null;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
}

export interface Message {
  id: string;
  userId: string;
  ideaId: string;
  role: "user" | "assistant";
  content: string;
  hidden: boolean;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
}

export interface Project {
  id: string;
  userId: string;
  name: string;
  repos: string[]; // array of "owner/name"
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
}

export interface Repo {
  slug: string; // "owner/name"
  userId: string;
  url: string;
  createdAt: number;
  updatedAt: number;
  deletedAt: number | null;
}

export interface SyncPayloadRow {
  id?: string;
  slug?: string;
  user_id: string;
  updated_at: number;
  deleted_at: number | null;
}

// Wire format used by the sync API (snake_case matching server D1 schema)
export interface WireIdea {
  id: string;
  user_id: string;
  title: string;
  brain_dump: string;
  status: IdeaStatus;
  project_id: string | null;
  preferred_repo: string | null;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface WireMessage {
  id: string;
  user_id: string;
  idea_id: string;
  role: "user" | "assistant";
  content: string;
  hidden: number; // 0 | 1
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface WireProject {
  id: string;
  user_id: string;
  name: string;
  repos: string; // JSON string
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface WireRepo {
  slug: string;
  user_id: string;
  url: string;
  created_at: number;
  updated_at: number;
  deleted_at: number | null;
}

export interface SyncRequest {
  since: number;
  changes: {
    ideas: WireIdea[];
    messages: WireMessage[];
    projects: WireProject[];
    repos: WireRepo[];
  };
}

export interface SyncResponse {
  serverTime: number;
  changes: {
    ideas: WireIdea[];
    messages: WireMessage[];
    projects: WireProject[];
    repos: WireRepo[];
  };
}

export interface AuthState {
  clientId: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  login: string | null;
  name: string | null;
}

export type SyncStatus =
  | { kind: "idle" }
  | { kind: "syncing" }
  | { kind: "synced"; at: number }
  | { kind: "offline" }
  | { kind: "error"; message: string };

export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// Conversion helpers: camelCase <-> wire format
export function ideaToWire(idea: Idea): WireIdea {
  return {
    id: idea.id,
    user_id: idea.userId,
    title: idea.title,
    brain_dump: idea.brainDump,
    status: idea.status,
    project_id: idea.projectId,
    preferred_repo: idea.preferredRepo,
    created_at: idea.createdAt,
    updated_at: idea.updatedAt,
    deleted_at: idea.deletedAt,
  };
}

export function ideaFromWire(w: WireIdea): Idea {
  return {
    id: w.id,
    userId: w.user_id,
    title: w.title,
    brainDump: w.brain_dump,
    status: w.status,
    projectId: w.project_id,
    preferredRepo: w.preferred_repo,
    createdAt: w.created_at,
    updatedAt: w.updated_at,
    deletedAt: w.deleted_at,
  };
}

export function messageToWire(m: Message): WireMessage {
  return {
    id: m.id,
    user_id: m.userId,
    idea_id: m.ideaId,
    role: m.role,
    content: m.content,
    hidden: m.hidden ? 1 : 0,
    created_at: m.createdAt,
    updated_at: m.updatedAt,
    deleted_at: m.deletedAt,
  };
}

export function messageFromWire(w: WireMessage): Message {
  return {
    id: w.id,
    userId: w.user_id,
    ideaId: w.idea_id,
    role: w.role,
    content: w.content,
    hidden: w.hidden === 1,
    createdAt: w.created_at,
    updatedAt: w.updated_at,
    deletedAt: w.deleted_at,
  };
}

export function projectToWire(p: Project): WireProject {
  return {
    id: p.id,
    user_id: p.userId,
    name: p.name,
    repos: JSON.stringify(p.repos),
    created_at: p.createdAt,
    updated_at: p.updatedAt,
    deleted_at: p.deletedAt,
  };
}

export function projectFromWire(w: WireProject): Project {
  return {
    id: w.id,
    userId: w.user_id,
    name: w.name,
    repos: (() => { try { return JSON.parse(w.repos) as string[]; } catch { return []; } })(),
    createdAt: w.created_at,
    updatedAt: w.updated_at,
    deletedAt: w.deleted_at,
  };
}

export function repoToWire(r: Repo): WireRepo {
  return {
    slug: r.slug,
    user_id: r.userId,
    url: r.url,
    created_at: r.createdAt,
    updated_at: r.updatedAt,
    deleted_at: r.deletedAt,
  };
}

export function repoFromWire(w: WireRepo): Repo {
  return {
    slug: w.slug,
    userId: w.user_id,
    url: w.url,
    createdAt: w.created_at,
    updatedAt: w.updated_at,
    deletedAt: w.deleted_at,
  };
}
